import { useMemo } from "react";

import type { IFiltersConfigCategoryItem } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/category";
import type { SurfaceSelection } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterDefinition";
import type { CategoryFiltersConfig } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";

import { createLeadsSearchFiltersConfig } from "../configs/createLeadsSearchFiltersConfig";
import { leadsCategories } from "../configs/filterCategory";
import type { CompanySelectObject } from "../types/description.dto";
import type { LeadsCapability } from "../types/leadsCapability";
import type { LeadsFilterCategory } from "../types/leadsFilterCategory";
import type { OrderedFilterInstance } from "../types/leadsSearchFilterInstance";

import { useLeadsFilterBuildContext } from "./useLeadsFilterBuildContext";

// The categories the leads domain owns, derived from the category config in canonical order.
// CRM is a separate domain (emitted by `useComputeCrmFiltersConfig`), so it is excluded here.
const LEADS_CATEGORIES: readonly IFiltersConfigCategoryItem[] = Object.values(leadsCategories).filter(
    (category) => category.id !== leadsCategories.crm.id,
);

interface UseComputeLeadsFiltersConfigParams {
    selection: SurfaceSelection<LeadsFilterCategory, LeadsCapability>;
    /** Surfaces that seed the suppression list on by default */
    applySuppressionListByDefault?: boolean;
    listCompanies?: readonly CompanySelectObject[];
}

export const useComputeLeadsFiltersConfig = ({
    selection,
    applySuppressionListByDefault = false,
    listCompanies,
}: UseComputeLeadsFiltersConfigParams): CategoryFiltersConfig<OrderedFilterInstance> => {
    const { ctx, ready, loading } = useLeadsFilterBuildContext({
        selection,
        applySuppressionListByDefault,
        listCompanies,
    });

    const { filtersList, status } = useMemo(
        () => createLeadsSearchFiltersConfig(ctx, ready, selection, loading),
        [ctx, ready, selection, loading],
    );

    const desiredCategoryIds = selection.categories;
    const categoriesList = useMemo<readonly IFiltersConfigCategoryItem[]>(() => {
        if (!desiredCategoryIds) {
            return LEADS_CATEGORIES;
        }

        const ids: ReadonlySet<string> = desiredCategoryIds;

        return LEADS_CATEGORIES.filter((c) => ids.has(c.id));
    }, [desiredCategoryIds]);

    return { filtersList, status, categoriesList };
};
